import React from 'react';
import { cn } from '../../lib/utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { generateExecutiveSummary, getDetailedRecommendations } from '../../utils/scoring';

const { FiAlertCircle, FiCheckCircle, FiInfo, FiZap, FiShield } = FiIcons;

export default function ScoreOverview({ results, businessInfo }) {
  const { percentage = 0, riskLevel = '', riskColor = 'text-slate-500', criticalAreas = [], strongAreas = [] } = results || {};
  const executiveSummary = generateExecutiveSummary(results);
  
  const priorities = [
    ...(criticalAreas[0] ? getDetailedRecommendations(criticalAreas[0].id) : []),
    ...(criticalAreas[1] ? getDetailedRecommendations(criticalAreas[1].id).slice(0, 2) : [])
  ].slice(0, 5);

  let StatusIcon = FiInfo;
  if (percentage <= 40) StatusIcon = FiAlertCircle;
  else if (percentage >= 86) StatusIcon = FiCheckCircle;

  return (
    <div className="space-y-8">
      {/* Main Score Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center relative overflow-hidden print-break-inside-avoid">
        <div className="absolute top-0 right-0 p-4 no-print">
           <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
             <SafeIcon icon={FiShield} className="text-blue-500" /> ProtegeTuRed by ComplianceBOX
           </div>
        </div>

        <h2 className="text-xl font-medium text-slate-500 mb-2">Resumen para {businessInfo?.companyName}</h2>
        
        <div className="relative inline-flex items-center justify-center w-48 h-48 my-6">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#f1f5f9" strokeWidth="8" />
            <circle 
              cx="50" cy="50" r="45" fill="none" stroke="currentColor" 
              strokeWidth="8" strokeDasharray="282.7" 
              strokeDashoffset={282.7 - (282.7 * percentage) / 100} 
              className={cn("transition-all duration-1000 ease-out", riskColor)}
            />
          </svg>
          <div className="absolute flex flex-col items-center justify-center">
            <span className="text-5xl font-extrabold text-slate-800">{percentage}</span>
            <span className="text-sm font-bold text-slate-400 uppercase tracking-tighter">Puntaje</span>
          </div>
        </div>

        <div className={cn("inline-flex items-center gap-2 px-6 py-2.5 rounded-full border-2 mb-4", riskColor, riskColor.replace('text-', 'bg-').replace('500', '50'), riskColor.replace('text-', 'border-').replace('500', '200'))}>
          <SafeIcon icon={StatusIcon} className="text-xl" />
          <span className="font-bold text-lg">{riskLevel}</span>
        </div>

        <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-2xl mx-auto">
          <p className="text-[10px] text-slate-500 italic leading-relaxed">
            <strong>Aviso:</strong> Este diagnóstico es educativo e informativo. No constituye una auditoría oficial, certificación ni asesoría legal.
          </p>
        </div>
      </div>
      
      {/* Rest of the component remains the same */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print-break-inside-avoid">
        <div className="lg:col-span-2 bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              <SafeIcon icon={FiInfo} />
            </div>
            Resumen Ejecutivo
          </h3>
          <p className="text-slate-700 leading-relaxed text-lg">
            {executiveSummary}
          </p>
          
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest block mb-2">Áreas Fuertes</span>
              <ul className="space-y-1">
                {strongAreas.length > 0 ? strongAreas.map(s => (
                  <li key={s.id} className="text-sm font-bold text-slate-700 flex justify-between">
                    <span>{s.title}</span>
                    <span className="text-emerald-600">{s.percentage}%</span>
                  </li>
                )) : <li className="text-xs text-slate-400 italic">No se detectaron áreas de fortaleza alta aún.</li>}
              </ul>
            </div>
            <div className="p-4 bg-red-50 rounded-xl border border-red-100">
              <span className="text-[10px] font-black text-red-600 uppercase tracking-widest block mb-2">Áreas Críticas</span>
              <ul className="space-y-1">
                {criticalAreas.length > 0 ? criticalAreas.map(s => (
                  <li key={s.id} className="text-sm font-bold text-slate-700 flex justify-between">
                    <span>{s.title}</span>
                    <span className="text-red-600">{s.percentage}%</span>
                  </li>
                )) : <li className="text-xs text-slate-400 italic">No se detectaron áreas críticas.</li>}
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl border border-slate-800">
          <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-blue-400">
            <SafeIcon icon={FiZap} />
            Top 5 Prioridades
          </h3>
          <ul className="space-y-4">
            {priorities.length > 0 ? priorities.map((task, idx) => (
              <li key={idx} className="flex items-start gap-3 group">
                <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5 border border-blue-500/30">
                  {idx + 1}
                </span>
                <span className="text-sm text-slate-300 group-hover:text-white transition-colors">{task}</span>
              </li>
            )) : <li className="text-sm text-slate-400 italic">No hay prioridades críticas detectadas.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}